import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Product } from '../../services/product';
import { ProductProcessingModal } from './ProductProcessingModal';
import { Eye, Image, Package, Database, CheckCircle2, Clock } from 'lucide-react';

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState(false);
  const hasImages = !!product.main_image_url || (product.gallery_images && product.gallery_images.length > 0);

  // Count unique images to avoid double-counting main image
  const imageCount = React.useMemo(() => {
    if (!product.gallery_images || product.gallery_images.length === 0) {
      return product.main_image_url ? 1 : 0;
    }

    // Extract unique image URLs from gallery_images
    const uniqueUrls = new Set<string>();
    product.gallery_images.forEach((img: any) => {
      const url = typeof img === 'string' ? img : img?.url;
      if (url) {
        uniqueUrls.add(url);
      }
    });

    return uniqueUrls.size;
  }, [product.gallery_images, product.main_image_url]);

  const handleViewProduct = () => {
    // Determine the correct return path based on current location
    const currentPath = location.pathname + location.search;
    let returnTo = '/products' + location.search; // default fallback
    
    // If we're on the bulk-review page, return to bulk-review
    if (location.pathname === '/products/bulk-review') {
      returnTo = '/products/bulk-review';
    }
    
    // Navigate to product detail and preserve current location in state
    navigate(`/products/${product.id}`, {
      state: { 
        from: currentPath,
        returnTo: returnTo
      }
    });
  };

  const handleProcessProduct = () => {
    setIsProcessingModalOpen(true);
  };

  const handleJobCreated = (jobId: string, isAsync?: boolean) => {
    setIsProcessingModalOpen(false);
    // You can add toast notifications here if needed
    console.log(`Job created: ${jobId}, async: ${isAsync}`);
  };

  return (
    <Card className="card-modern-enhanced group hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 flex flex-col h-full overflow-hidden relative">
      <CardHeader className="p-0">
        {/* Product image */}
        <div className="relative aspect-square bg-gradient-to-br from-muted/50 to-muted rounded-t-xl overflow-hidden">
          {product.main_image_url ? (
            <img
              src={product.main_image_url}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <Package className="h-16 w-16 group-hover:scale-110 transition-transform duration-300" />
            </div>
          )}
          
          {/* Modern overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
          
          {/* Shimmer effect on hover */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 transform -skew-x-12 -translate-x-full group-hover:translate-x-full" 
               style={{ transition: 'transform 0.7s ease-out, opacity 0.3s' }} />
          
          {/* Image count badge */}
          {hasImages && (
            <div className="absolute top-3 right-3 bg-black/90 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center font-medium shadow-xl border border-white/10 group-hover:scale-110 transition-transform duration-300">
              <Image className="h-3 w-3 mr-1.5" />
              {imageCount}
            </div>
          )}
          
          {/* Status badge */}
          {product.status && (
            <div className="absolute top-3 left-3 group-hover:scale-110 transition-transform duration-300">
              {product.status === 'enabled' ? (
                <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-xl border border-white/20 animate-pulse-subtle">
                  active
                </span>
              ) : (
                <Badge variant="secondary" className="shadow-md">
                  {product.status}
                </Badge>
              )}
            </div>
          )}

          {/* Transformation status badge */}
          {product.transformation_status && product.transformation_status !== 'untransformed' && (
            <div className="absolute bottom-3 left-3 group-hover:scale-110 transition-transform duration-300">
              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold shadow-xl border border-white/20 ${
                product.transformation_status === 'complete'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                  : 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white'
              }`}>
                {product.transformation_status === 'complete' ? (
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                ) : (
                  <Clock className="h-3 w-3 mr-1" />
                )}
                {product.transformation_status === 'complete' ? 'Transformed' : 'Partial'}
              </span>
            </div>
          )}

          {/* No images overlay */}
          {!hasImages && (
            <div className="absolute inset-0 bg-gradient-to-br from-muted-foreground/80 to-muted-foreground/60 flex items-center justify-center backdrop-blur-sm">
              <div className="text-center text-white px-4 group-hover:scale-105 transition-transform duration-300">
                <Package className="h-12 w-12 mx-auto mb-3 opacity-80 group-hover:animate-bounce" />
                <div className="text-sm font-medium">
                  No images available
                </div>
                <div className="text-xs opacity-80 mt-1">
                  for processing
                </div>
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-6 flex flex-col flex-1">
        <div className="flex-1 space-y-4">
          {/* Product info */}
          <div className="space-y-2">
            <CardTitle className="text-lg line-clamp-2 font-semibold leading-tight">
              {product.name}
            </CardTitle>
            <CardDescription className="text-sm font-medium text-muted-foreground">
              SKU: {product.sku}
            </CardDescription>
          </div>

          {/* Metadata */}
          <div className="space-y-2">
            {/* Sync info */}
            {product.last_synced && (
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <div className="w-1.5 h-1.5 bg-success rounded-full"></div>
                Last synced: {new Date(product.last_synced).toLocaleDateString()}
              </div>
            )}

            {/* Store info */}
            {product.stores && (
              <div className="text-xs text-muted-foreground">
                <span className="font-medium">Store:</span> {product.stores.name}
              </div>
            )}

            {/* Stock info */}
            {(product.stock_quantity !== null && product.stock_quantity !== undefined) && (
              <div className="flex items-center justify-between text-xs pt-1 border-t">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Database className="h-3 w-3" />
                  <span className="font-medium">Stock:</span>
                  <span className="font-semibold text-foreground">{product.stock_quantity}</span>
                </div>
                <Badge
                  variant={product.stock_quantity > 0 ? 'default' : 'secondary'}
                  className={`text-[10px] h-5 ${
                    product.stock_quantity > 0
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {product.stock_quantity > 0 ? 'In Stock' : 'Out of Stock'}
                </Badge>
              </div>
            )}
          </div>

          {/* Categories */}
          {product.category && (
            <div className="flex flex-wrap gap-1.5">
              {product.category.split(',').slice(0, 2).map((cat, index) => (
                <Badge key={index} variant="outline" className="text-xs font-medium">
                  {cat.trim()}
                </Badge>
              ))}
              {product.category.split(',').length > 2 && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  +{product.category.split(',').length - 2}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Actions - always at bottom */}
        <div className="flex gap-2 pt-4 mt-auto">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 hover:bg-muted/50 hover:scale-105 hover:-translate-y-0.5 transition-all duration-200"
            onClick={handleViewProduct}
          >
            <Eye className="h-3 w-3 mr-1.5 group-hover:animate-pulse" />
            View
          </Button>
          <Button 
            size="sm" 
            className="flex-1 shadow-md hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 transition-all duration-200 relative overflow-hidden"
            disabled={!hasImages}
            onClick={handleProcessProduct}
          >
            <span className="relative z-10 flex items-center">
              Process
            </span>
            {hasImages && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300 transform -skew-x-12 -translate-x-full group-hover:translate-x-full" />
            )}
          </Button>
        </div>
      </CardContent>
      
      {/* Processing Modal */}
      <ProductProcessingModal
        product={product}
        isOpen={isProcessingModalOpen}
        onClose={() => setIsProcessingModalOpen(false)}
        onJobCreated={handleJobCreated}
      />
    </Card>
  );
}